import { memo } from 'react';
import { createPortal } from 'react-dom';
import type { DocumentResponse } from '@vagusx/clients';
import { formatDate } from '@/lib/format';
import { isSectionVisible } from './constants';

/**
 * Mounts the print view as a portal on `document.body`, hidden on
 * screen via inline `display: none` and unhidden inside @media print
 * (see the `[data-print-host]` rules in index.css). Single helper used
 * by every surface that needs to print an extraction — the task work
 * panel and the standalone detail page — so the portal wiring + the
 * `data-print-host` attribute live in one place.
 *
 * Usage: render unconditionally; the parent surface owns the action
 * that calls `window.print()`. Browsers snapshot the portal content
 * even though it's display:none on screen, because @media print flips
 * its display to block.
 */
export function DocumentPrintHost(props: {
  document: DocumentResponse;
  attachmentLabels?: string[];
}): React.ReactElement {
  return createPortal(
    <div data-print-host style={{ display: 'none' }}>
      <DocumentPrintView {...props} />
    </div>,
    document.body
  );
}

// ⚠️ SYNC WITH FORM VIEW
// The paper-friendly view here AND `DocumentDataPanel.tsx`
// render the same extraction data. Anytime you add/remove/rename a field
// or section there — especially a new `<Section>` block or a new
// `form.register(...)` line — update this file too. Both use
// `isSectionVisible()` from constants.ts so classification gating stays
// in sync automatically, but the per-section field list is hand-kept.

interface DocumentPrintViewProps {
  document: DocumentResponse;
  /**
   * Labels applied to the attachment (e.g. ["Referral"]). Shown as a
   * single inline line at the top — the screen view uses a pill bar with
   * remove-buttons and a help icon, none of which belong on paper.
   */
  attachmentLabels?: string[];
}

/**
 * Paper-friendly read-only rendering of an extracted document. Rendered
 * inside the print portal (see `DocumentTabContent`) which is
 * display:none on screen and unhidden inside @media print — the
 * interactive form stays on screen untouched. Empty fields and
 * classification-filtered sections are skipped entirely so a printed
 * authorization reads like a compact summary, not a blank form.
 *
 * Layout rules:
 *   - Grid of label-above-value rows, 3 columns on narrow sections, 1
 *     col for long identifiers / addresses / notes that need width.
 *   - Diagnosis / service codes rendered as text tables — avoids the
 *     on-screen Badge component which prints as an empty rounded box
 *     on many printers.
 *
 * Memoized because the parent TaskWorkPanel re-renders on every
 * attachment mutation + form keystroke that bubbles up; our props are
 * React Query cache references and a stable array, so referential
 * equality is a safe skip.
 */
export const DocumentPrintView = memo(function DocumentPrintView({
  document: d,
  attachmentLabels,
}: DocumentPrintViewProps): React.ReactElement {
  // Computed once per render so the header timestamp and the footer
  // timestamp agree to the millisecond. Pinned to en-US for the same
  // reason as the footer — see comment there.
  const printedAt = new Date().toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  return (
    <div className="text-[11pt] leading-snug text-black">
      {/* Page header — patient + classification + print timestamp.
       * Print timestamp also appears in the footer (audit trail), but
       * users were missing it down there; surfacing it next to the
       * classification puts it where the eye lands first. Auth # is
       * rendered in its own full-width row inside the Authorization
       * section so a long payor ID has room to breathe. */}
      <div className="mb-4 border-b border-gray-400 pb-2">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-base font-semibold">{d.patientName ?? 'Unknown patient'}</h1>
          <div className="text-right text-[9pt] text-gray-600">
            {d.classification && <div className="capitalize">{d.classification}</div>}
            <div>Printed: {printedAt}</div>
          </div>
        </div>
        {attachmentLabels && attachmentLabels.length > 0 && (
          <div className="mt-1 text-[9pt] text-gray-700">
            <span className="font-medium">Labels:</span> {attachmentLabels.join(', ')}
          </div>
        )}
      </div>

      {isSectionVisible('authorization', d.classification) && (
        <PrintSection title="Authorization">
          {/* Auth # gets its own full-width row — payor-issued IDs can
           * be 20+ chars and collide with adjacent columns in Grid3. */}
          <Grid1>
            <Field label="Auth #" value={d.authNumber} />
          </Grid1>
          <Grid3>
            <Field label="Status" value={d.status} capitalize />
            <Field label="Urgency" value={d.urgency} capitalize />
            <Field label="Valid From" value={formatDate(d.validFrom)} />
            <Field label="Valid To" value={formatDate(d.validTo)} />
          </Grid3>
        </PrintSection>
      )}

      {isSectionVisible('patient', d.classification) && (
        <PrintSection title="Patient">
          <Grid1>
            <Field label="Name" value={d.patientName} />
          </Grid1>
          <Grid3>
            <Field label="DOB" value={formatDate(d.patientDob)} />
            <Field label="Member ID" value={d.patientMemberId} />
            <Field label="Phone" value={d.patientPhone} />
          </Grid3>
          <Grid1>
            <Field label="Address" value={d.patientAddress} />
          </Grid1>
        </PrintSection>
      )}

      {isSectionVisible('referringProvider', d.classification) && (
        <PrintSection title="Referring Provider">
          <Grid1>
            <Field label="Name" value={d.referringProviderName} />
          </Grid1>
          <Grid3>
            <Field label="NPI" value={d.referringProviderNpi} />
            <Field label="Phone" value={d.referringProviderPhone} />
            <Field label="Fax" value={d.referringProviderFax} />
          </Grid3>
        </PrintSection>
      )}

      {isSectionVisible('referringPractice', d.classification) && (
        <PrintSection title="Referring Practice">
          <Grid2>
            <Field label="Name" value={d.referringPracticeName} />
            <Field label="Fax" value={d.referringPracticeFax} />
          </Grid2>
        </PrintSection>
      )}

      {isSectionVisible('pcp', d.classification) && (
        <PrintSection title="Primary Care Physician">
          <Grid3>
            <Field label="Name" value={d.pcpName} />
            <Field label="NPI" value={d.pcpNpi} />
            <Field label="Phone" value={d.pcpPhone} />
          </Grid3>
        </PrintSection>
      )}

      {isSectionVisible('renderingProvider', d.classification) && (
        <PrintSection title="Rendering Provider">
          <Grid2>
            <Field label="Name" value={d.renderingProviderName} />
            <Field label="NPI" value={d.renderingProviderNpi} />
          </Grid2>
        </PrintSection>
      )}

      {isSectionVisible('renderingPractice', d.classification) && (
        <PrintSection title="Rendering Practice">
          <Grid2>
            <Field label="Name" value={d.renderingPracticeName} />
            <Field label="Fax" value={d.renderingPracticeFax} />
          </Grid2>
        </PrintSection>
      )}

      {isSectionVisible('insurance', d.classification) && (
        <PrintSection title="Insurance / Payer">
          <Grid2>
            <Field label="Health Plan" value={d.healthPlan} />
            <Field label="Network" value={d.insuranceNetwork} />
          </Grid2>
        </PrintSection>
      )}

      {isSectionVisible('datesOfService', d.classification) && (
        <PrintSection title="Dates of Service">
          <Grid2>
            <Field label="From" value={formatDate(d.dateOfServiceFrom)} />
            <Field label="To" value={formatDate(d.dateOfServiceTo)} />
          </Grid2>
        </PrintSection>
      )}

      {isSectionVisible('requestId', d.classification) && d.requestId && (
        <PrintSection title="Request ID / Number">
          <Grid1>
            <Field label="Request ID" value={d.requestId} />
          </Grid1>
        </PrintSection>
      )}

      {isSectionVisible('denialId', d.classification) && d.denialId && (
        <PrintSection title="Denial ID / Number">
          <Grid1>
            <Field label="Denial ID" value={d.denialId} />
          </Grid1>
        </PrintSection>
      )}

      {isSectionVisible('diagnosisCodes', d.classification) &&
        (d.diagnosisCodes?.length ?? 0) > 0 && (
          <PrintSection title="Diagnosis Codes">
            <table className="w-full border-collapse text-[10pt]">
              <tbody>
                {d.diagnosisCodes!.map((dx, i) => (
                  // Composite key — code repeats are common (multi-row
                  // referrals where every row is "REFERRAL"), and a bare
                  // index misbehaves on extractor reorder. The pair gives
                  // both reorder stability and within-list uniqueness.
                  <tr key={`${dx.code ?? 'dx'}-${i}`}>
                    <td className="w-24 border-b border-gray-300 py-1 pr-2 font-mono align-top">
                      {dx.code}
                    </td>
                    <td className="border-b border-gray-300 py-1 align-top">
                      {dx.description ?? ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PrintSection>
        )}

      {isSectionVisible('serviceCodes', d.classification) && (d.serviceCodes?.length ?? 0) > 0 && (
        <PrintSection title="Service Codes">
          <table className="w-full border-collapse text-[10pt]">
            <thead>
              <tr className="border-b border-gray-500 text-left">
                <th className="w-24 py-1 pr-2 font-medium">Code</th>
                <th className="py-1 pr-2 font-medium">Description</th>
                <th className="w-12 py-1 pr-2 font-medium">Qty</th>
                <th className="w-16 py-1 pr-2 font-medium">POS</th>
                <th className="w-36 py-1 font-medium">Effective</th>
              </tr>
            </thead>
            <tbody>
              {d.serviceCodes!.map((svc, i) => (
                <tr key={`${svc.code ?? 'svc'}-${i}`} className="border-b border-gray-300">
                  <td className="py-1 pr-2 font-mono align-top">{svc.code}</td>
                  <td className="py-1 pr-2 align-top">{svc.description ?? ''}</td>
                  <td className="py-1 pr-2 align-top">{svc.units ?? ''}</td>
                  <td className="py-1 pr-2 align-top">{svc.placeOfService ?? ''}</td>
                  <td className="py-1 align-top text-[9pt]">
                    {svc.effectiveFrom || svc.effectiveTo
                      ? [formatDate(svc.effectiveFrom), formatDate(svc.effectiveTo)]
                          .filter(Boolean)
                          .join(' – ')
                      : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </PrintSection>
      )}

      {isSectionVisible('notes', d.classification) && d.notes && (
        <PrintSection title="Notes">
          <p className="whitespace-pre-wrap text-[10pt]">{d.notes}</p>
        </PrintSection>
      )}

      {/* Footer — gives the printed page a clear audit tail. Pinned to
       * en-US so a printed doc reads the same whether the browser is in
       * US, DE, JP, etc. — reduces confusion when the fax recipient sees
       * a different date format than the sender. */}
      <div className="mt-6 border-t border-gray-400 pt-2 text-[8pt] text-gray-600">
        Printed from VagusX · {printedAt}
      </div>
    </div>
  );
});

function PrintSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="mb-3 break-inside-avoid">
      <h2 className="mb-1 text-[10pt] font-semibold uppercase tracking-wide text-gray-700">
        {title}
      </h2>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function Grid1({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="grid grid-cols-1 gap-x-6 gap-y-1">{children}</div>;
}
function Grid2({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="grid grid-cols-2 gap-x-6 gap-y-1">{children}</div>;
}
function Grid3({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div className="grid grid-cols-3 gap-x-6 gap-y-1">{children}</div>;
}

function Field({
  label,
  value,
  capitalize,
}: {
  label: string;
  value: string | null | undefined;
  capitalize?: boolean;
}): React.ReactElement | null {
  // Blank fields earn no ink. Keeping them out drops a typical auth from
  // three pages to one and avoids the "mostly empty boxes" look of the
  // on-screen form.
  if (!value || !value.trim()) return null;
  // Stacked layout — label on top in a small caps-like style, value
  // underneath with the full column width. Inline "Label: value" rows
  // inside a 3-column grid overflow when values are long (20-digit auth
  // numbers, full addresses, long practice names), which printed as
  // overlapping text on top of the next column. `break-words` is the
  // seat-belt for the occasional value that still exceeds the column.
  return (
    <div className="min-w-0">
      <div className="text-[8pt] font-medium uppercase tracking-wide text-gray-600">{label}</div>
      <div className={`break-words ${capitalize ? 'capitalize' : ''}`}>{value}</div>
    </div>
  );
}
